// Tenantra Inc. - API Service
import axios from 'axios';
import { getAccessToken, refreshAccessToken, clearAuth } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retried, try to refresh token (skip for auth endpoints themselves)
    const isAuthRequest = /\/auth\/(login|register|refresh|mfa)/.test(originalRequest?.url || '');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;
      
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const apiHelpers = {
  // Handle API errors consistently
  handleError(error) {
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail.map(d => (d?.msg || '').replace(/^Value error, /, '')).filter(Boolean).join(' ') || 'Please check the form and try again';
    }
    if (detail) {
      return typeof detail === 'string' ? detail : (detail.message || 'Please check the form and try again');
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },
  
  // Format validation errors
  formatValidationErrors(error) {
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(err => ({
        field: err.loc?.slice(-1)[0],
        message: err.msg
      }));
    }
    return [];
  }
};
